import React, { useEffect, useState, useRef } from 'react';
import * as d3 from 'd3';
import { useDashboard } from '../../model/DashboardContext';
import { USMapInteractionData, USMapTooltip } from "../geographic/USMapToolTip";
import { StateElectionDataEntry } from "../../model/data/StateElectionDataEntry";
import USElectionData from "../../model/data/USElectionData";

interface TableData {
    state: string;
    year: number;
    ethnicityPercentage: number;
    adjustedLead: number;
    state_data: StateElectionDataEntry;
}

const ScatterPlotView: React.FC<{ xScale: number; yScale: number }> = ({ xScale, yScale }) => {
    const { dashboard, updateSelectedStates } = useDashboard();
    const [tableData, setTableData] = useState<TableData[]>([]);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [hoveredCell, setHoveredCell] = useState<USMapInteractionData | null>(null);

    // Function to calculate the best fit line (slope and intercept)
    const calculateLinearRegression = (data: TableData[]) => {
        const n = data.length;
        const sumX = data.reduce((sum, d) => sum + d.adjustedLead, 0);
        const sumY = data.reduce((sum, d) => sum + d.ethnicityPercentage, 0);
        const sumXY = data.reduce((sum, d) => sum + d.adjustedLead * d.ethnicityPercentage, 0);
        const sumX2 = data.reduce((sum, d) => sum + d.adjustedLead * d.adjustedLead, 0);

        const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;

        return { slope, intercept };
    };


    function addSpaceBetweenLowerAndUpper(text: string): string {
        return text.replace(/([a-z])([A-Z])/g, "$1 $2");
      }

    useEffect(() => {
        if (dashboard) {
            const demographicData = dashboard.demographicData;
            const selectedElectionData = dashboard.selectedElectionData;
            const selectedEthnicities = dashboard.selectedEthnicities;

            if (demographicData && selectedElectionData) {
                const processedData = Object.values(selectedElectionData.data)
                    .filter((entry: any) => entry.state_name !== 'Totals')
                    .map((entry: any) => {
                        const state: string = entry.state_name;
                        const winner = entry.winner === 'dem' ? -1 : 1;
                        const pointLead = entry.point_lead;
                        const ethnicityPercentages = demographicData.getValuesForYearAndEthnicitiesAndState(
                            selectedElectionData.year,
                            selectedEthnicities,
                            state
                        );

                        const ethnicityPercentage = Array.from(ethnicityPercentages.values())
                            .reduce((sum, value) => sum + (value as number), 0);

                        return {
                            state: state,
                            year: selectedElectionData.year,
                            ethnicityPercentage: ethnicityPercentage,
                            adjustedLead: winner * pointLead,
                            state_data: entry
                        } as TableData;
                    });

                setTableData(processedData);
            }
        }
    }, [dashboard]);

    useEffect(() => {
        if (tableData.length > 0 && svgRef.current && tooltipRef.current) {
            const svg = d3.select(svgRef.current);
            svg.selectAll("*").remove(); // Clear previous content

            const width = 800;
            const height = 250;
            const margin = { top: 20, right: 30, bottom: 40, left: 50 };

            const maxAbsLead = Math.max(
                Math.abs(d3.min(tableData, d => d.adjustedLead) || 0),
                Math.abs(d3.max(tableData, d => d.adjustedLead) || 0)
            );

            const x = d3.scaleLinear()
                .domain([-maxAbsLead, maxAbsLead])
                .range([margin.left, width - margin.right]);

            const y = d3.scaleLinear()
                .domain([0, d3.max(tableData, d => d.ethnicityPercentage) || 0])
                .range([height - margin.bottom, margin.top]);

            // Add axes
            svg.append("g")
                .attr("transform", `translate(0,${height - margin.bottom})`)
                .call(g => g.call(d3.axisBottom(x).ticks(width / 80).tickSizeOuter(0)));

            svg.append("g")
                .attr("transform", `translate(${margin.left},0)`)
                .call(g => g.call(d3.axisLeft(y)));

            // Add axis labels
            svg.append("text")
                .attr("class", "x-axis-label")
                .attr("text-anchor", "end")
                .attr("x", width - margin.right)
                .attr("y", height - 6)
                .text("Winning Party's Percentage Lead (Dem: negative, Rep: positive)")
                .attr("font-weight", "800")
                .attr("font-size", "12px");

            svg.append("text")
                .attr("class", "y-axis-label")
                .attr("text-anchor", "middle")
                .attr("x", -height / 2)
                .attr("y", margin.left - 30)
                .attr("transform", "rotate(-90)")
                .text("Percentage of Selected Ethnicities")
                .attr("font-size", "12px")
                .attr("font-weight", "800");

            // Create point groups (circles + labels)
            const pointGroups = svg.append("g")
                .selectAll("g")
                .data(tableData)
                .join("g")
                .attr("transform", d => `translate(${x(d.adjustedLead)},${y(d.ethnicityPercentage)})`);

            // Add circles to groups
            pointGroups.append("circle")
                .attr("r", 6.5)
                .attr("fill", d => (dashboard?.colorScale ? dashboard.colorScale(d.adjustedLead) : "grey"))
                .attr("stroke", "black")
                .attr("stroke-width", d => dashboard?.selectedStates.has(d.state) ? 2 : 0.5)
                .style("cursor", "default");

            // Add text labels to groups
            pointGroups.append("text")
                .attr("dy", "0.35em")
                .attr("text-anchor", "middle")
                .text(d => USElectionData.getStateInitials(d.state))
                .attr("font-size", "8px")
                .attr("font-weight", "bold")
                .attr("fill", "black")
                .style("user-select", "none")
                .style("cursor", "default");

            // Add interactivity to the groups
            pointGroups
                .on("mouseover", (event, d) => {
                    const state_data = d.state_data;
                    const data = [
                        `${addSpaceBetweenLowerAndUpper(state_data.state_name)}: ${state_data.winner} won by ${state_data.point_lead}%`,
                        `Total votes: ${Math.round(state_data.total_votes/1000)}k`,
                        `Dem percentage: ${state_data.dem_percentage}%`,
                        `Rep percentage: ${state_data.rep_percentage}%`,
                        `Dem electoral votes: ${state_data.dem_electoral_votes}`,
                        `Rep electoral votes: ${state_data.rep_electoral_votes}`,
                        `Number of voters per electoral vote: ${Math.round((state_data.num_voters_per_electoral_vote)/1000)}k`,
                        `Year: ${d.year}`
                    ];

                    const mousePosition = { x: event.clientX + 30, y: event.clientY };

                    setHoveredCell({
                        data: data,
                        value: d.adjustedLead,
                        position: mousePosition
                    } as USMapInteractionData);
                })
                .on("mousemove", (event) => {
                    const [x, y] = d3.pointer(event);
                    tooltipRef.current!.style.transform = `translate(${x}px, ${y}px)`;
                })
                .on("mouseout", () => {
                    setHoveredCell(null);
                })
                .on("click", (event, d) => {
                    if (dashboard?.selectedStates.has(d.state)) {
                        dashboard.selectedStates.delete(d.state);
                    } else {
                        dashboard?.selectedStates.add(d.state);
                    }
                    updateSelectedStates(new Set(dashboard?.selectedStates));
                });

            // Add regression line
            const { slope, intercept } = calculateLinearRegression(tableData);
            svg.append("line")
                .attr("x1", x(d3.min(tableData, d => d.adjustedLead) || 0))
                .attr("x2", x(d3.max(tableData, d => d.adjustedLead) || 0))
                .attr("y1", y((d3.min(tableData, d => d.adjustedLead) || 0) * slope + intercept))
                .attr("y2", y((d3.max(tableData, d => d.adjustedLead) || 0) * slope + intercept))
                .attr("stroke", "#A18FDB")
                .attr("stroke-width", 2);

            // Add vertical dashed line
            const middleX = x(0);
            svg.append("line")
                .attr("x1", middleX)
                .attr("x2", middleX)
                .attr("y1", margin.top)
                .attr("y2", height - margin.bottom)
                .attr("stroke", "gray")
                .attr("stroke-width", 1)
                .attr("stroke-dasharray", "5, 5");

            // Apply scaling transformation
            svg.attr('transform', `scale(${xScale}, ${yScale})`);
        }
    }, [dashboard, tableData, updateSelectedStates, xScale, yScale]);

    return (
        <div>
            <svg ref={svgRef} width="800" height="250"></svg>
            <div ref={tooltipRef}></div>
            <USMapTooltip
                data={hoveredCell?.data || []}
                position={hoveredCell?.position || { x: 0, y: 0 }}
                width={10000}
                height={250}
            />
        </div>
    );
};

export default ScatterPlotView;